import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, CloudUpload, Loader2 } from "lucide-react";
import { useAssessment } from "@/context/AssessmentContext";

/*
  Phase 8C.1 — accessible save-status indicator.

  Reads:
   * lastLocalSavedAt  — timestamp of most recent localStorage write
   * localSaveError    — true when the browser refused persistence
   * serverSavedAt     — set only when the opt-in server save succeeded

  Behaviour:
   * When a state change has just happened, briefly shows "Saving…".
   * Otherwise shows "Saved locally · <time>".
   * Only shows "Saved to server" when serverSavedAt is set.
   * On error shows a distinct warning.
*/
function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function SaveStatusPill() {
  const { lastLocalSavedAt, localSaveError, serverSavedAt } = useAssessment();
  const [showSaving, setShowSaving] = useState(false);

  useEffect(() => {
    if (!lastLocalSavedAt) return;
    setShowSaving(true);
    const t = setTimeout(() => setShowSaving(false), 400);
    return () => clearTimeout(t);
  }, [lastLocalSavedAt]);

  if (localSaveError) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-sm border border-red-400/50 bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-900"
        role="status"
        aria-live="polite"
        data-testid="save-status-pill"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        Save failed — enable browser storage
      </span>
    );
  }

  if (showSaving) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="save-status-pill"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving…
      </span>
    );
  }

  if (!lastLocalSavedAt) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground"
        role="status"
        data-testid="save-status-pill"
      >
        Not saved yet — start answering to save automatically
      </span>
    );
  }

  return (
    <span
      className="inline-flex flex-wrap items-center gap-1.5 rounded-sm border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground"
      role="status"
      aria-live="polite"
      data-testid="save-status-pill"
      title="Progress is stored in this browser only. You can close this page and return later."
    >
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
      <span className="text-foreground">Saved locally</span>
      <span aria-hidden="true">·</span>
      <span>Last saved {formatTime(lastLocalSavedAt)}</span>
      {serverSavedAt && (
        <>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1 text-foreground">
            <CloudUpload className="h-3 w-3" />
            Saved to server {formatTime(serverSavedAt)}
          </span>
        </>
      )}
    </span>
  );
}
